import { formatNumber } from '@/utils';

/**
 * Small helpers every payroll screen needs. They exist so amounts, employee
 * selections and template downloads are handled the same way everywhere.
 */

/** Reads an amount the API may send as a number, a numeric string or null. */
export const toAmount = (value: unknown): number => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

/** Money in BDT, or `fallback` when the API sent nothing at all. */
export const formatBDT = (value: unknown, fallback = 'n/a') =>
  value == null || value === ''
    ? fallback
    : formatNumber(toAmount(value), { currency: 'BDT' });

/**
 * The employee picker stores `{ [department]: userId[] }`; the API wants a
 * flat list of user ids.
 */
export const flattenAssigned = (assigned?: Record<string, string[]> | null) =>
  Object.values(assigned || {}).flat();

/** The reverse of `flattenAssigned`, for filling the picker from saved ids. */
export const groupUserIdsByDepartment = (
  userIds: string[] = [],
  employees: { user_id: string; department?: { name?: string } | null }[] = [],
) =>
  userIds.reduce<Record<string, string[]>>((groups, userId) => {
    const employee = employees.find((item) => item.user_id === userId);
    if (!employee) return groups;
    const department = employee.department?.name || 'No Department';
    (groups[department] ||= []).push(userId);
    return groups;
  }, {});

/** Saves a blob URL returned by a template endpoint, then releases it. */
export const saveBlobUrl = (blobUrl: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};
